import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronRightBold } from '@sorare/core/src/atoms/icons/ChevronRightBold';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandXS, LabelS } from '@sorare/core/src/atoms/typography';
import { FOOTBALL_MARKET_INSTANT_BUY } from '@sorare/core/src/constants/__generated__/routes';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { onboarding } from '@sorare/core/src/lib/glossary';

import { ExpertModeWarningDialog } from 'components/commonGame/UnlockPro/ExpertModeWarningDialog';

import type {
  expertBannerSkipOnboardingMutation,
  expertBannerSkipOnboardingMutationVariables,
} from './__generated__/index.graphql';

const EXPERT_BANNER_SKIP_ONBOARDING_MUTATION = gql`
  mutation expertBannerSkipOnboardingMutation($input: skipOnboardingInput!) {
    skipOnboarding(input: $input) {
      currentUser {
        slug
        forcedIntoOnboarding
      }
      errors {
        message
        code
      }
    }
  }
` as TypedDocumentNode<
  expertBannerSkipOnboardingMutation,
  expertBannerSkipOnboardingMutationVariables
>;

const Root = styled(ButtonBase)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--double-unit);
  padding: var(--double-unit);
  border-radius: var(--unit);
  background: var(--c-nl-500);
  &:hover {
    background: var(--c-nl-300);
  }
  transition: 300ms background-color ease-in-out;
`;
const TextWrapper = styled(Vertical).attrs({ gap: 0.5 })`
  align-items: flex-start;
`;

export const ExpertBanner = () => {
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [skipOnboarding] = useMutation(EXPERT_BANNER_SKIP_ONBOARDING_MUTATION, {
    showErrorsWithSnackNotification: true,
  });
  const navigate = useNavigate();
  const getInteractionContext = useGetInteractionContext();

  return (
    <>
      <Root onClick={() => setWarningDialogOpen(true)}>
        <TextWrapper>
          <BrandXS>
            <FormattedMessage {...onboarding.expert} />
          </BrandXS>
          <LabelS color="var(--c-nd-700)">
            <FormattedMessage {...onboarding.expertDescription} />
          </LabelS>
        </TextWrapper>
        <ChevronRightBold />
      </Root>
      {!!warningDialogOpen && (
        <ExpertModeWarningDialog
          onClose={() => setWarningDialogOpen(false)}
          onContinue={async () => {
            setWarningDialogOpen(false);
            await skipOnboarding({
              variables: {
                input: {
                  sport: Sport.FOOTBALL,
                  interactionContext: getInteractionContext(),
                },
              },
            });
            navigate(FOOTBALL_MARKET_INSTANT_BUY);
          }}
        />
      )}
    </>
  );
};
